import React, { useState } from 'react'
import BulkExportModal, { type ExportConfig } from '../../../ReportsFeatureV2/components/BulkExportModal'
import { notification } from '../../../../components/Notifications'
import { Box, Chip, Divider, List, ListItem, ListItemButton, Popover, Stack, useMediaQuery, useTheme, type SxProps, type Theme } from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import GenericButton from '../../../../components/GenericButton'
import { ButtonType } from '../../../../components/GenericButton/styles'
import DisplayCustomizationDrawer from '../DisplayCustomizationDrawer'
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined'
import ReportActionsMenu from '../ReportActionsMenu'
import ReportMutationAction from '../ReportMutationAction'
import { type ReportActionKind } from '../reportActionCopy'
import SandboxReportsViewModal from '../SandboxReportsViewModal'
import { ReportControlButtons } from './styles'
import { type ReportsV2 } from '../../../../models/Report'
import { type SurveyCompletionRate } from '../../../../models/Division'
import { type SlotStatus } from '../../../../models/DistributionPlan'
import CustomTooltip from '../../../../components/CustomTooltip'
import { useReportsContext } from '../../ReportsContext'
import { type DisplaySettings } from '../../types'
import { type ReportAccessLevel } from '../../../../APIManager/UserManager'
import { useUserRole } from '../../../../hooks/useUserRole'
import { ROLES } from '../../../../consts/roles'
import { useSandboxReportsView } from '../../../../hooks/useSandboxReportsView'

export interface ReportControlsProps {
  downloadCSVFile: (exportConfig?: ExportConfig) => Promise<any>
  downloadPDFReport: (exportConfig?: ExportConfig) => Promise<any>
  requestForCourseSectionFilesInfo?: Record<string, any>
  disableButtons?: boolean
  reports: ReportsV2 | null
  fetchReports: (params: Record<string, any>) => Promise<any>
  onSubmitDisplaySettings?: (settings: DisplaySettings) => Promise<void>
  disableOption?: boolean
  selectedTab?: string
  percent?: number
  isBulkExportModalOpen: boolean
  onOpenExportModal: () => void
  onCloseExportModal: () => void
  initialExportBy?: string
  lockExportBy?: boolean
  hideMoreButton?: boolean
  // Regenerate Report (Account Owner only) — hidden on the instructor dashboard.
  hideRegenerate?: boolean
  // True when the current report scope is a Course Section (vs an Academic Unit).
  isSectionReport?: boolean
  // The current report's completion-rate row — names the regenerate/revert target (title,
  // survey project, term) and carries the soft-delete version metadata that gates the
  // revert/restore menu items. Passed straight through to ReportActionsMenu.
  reportMeta?: SurveyCompletionRate | null
  // Section row status carried from the CDG children-list row for section report actions.
  sectionSlotStatus?: SlotStatus | null
  extraControls?: React.ReactNode
  onSavedComments?: () => void
  bulkExportModalConfig?: Partial<React.ComponentProps<typeof BulkExportModal>>
  /** False under `view_results_only` — hides the CSV file-type option for survey-results exports. */
  allowResultsCsvExport?: boolean
  /** The caller's resolved report-access level for the current report — passed through to unlock Individual Responses for full_access non-global roles. */
  reportAccessLevel?: ReportAccessLevel | null
}

const ReportsControls: React.FC<ReportControlsProps> = ({
  disableButtons,
  downloadCSVFile,
  downloadPDFReport,
  requestForCourseSectionFilesInfo,
  reports,
  fetchReports,
  onSubmitDisplaySettings,
  disableOption,
  selectedTab,
  percent,
  isBulkExportModalOpen,
  onOpenExportModal,
  onCloseExportModal,
  initialExportBy = 'Aggregate Report',
  lockExportBy = false,
  hideMoreButton = false,
  hideRegenerate = false,
  isSectionReport = false,
  reportMeta,
  sectionSlotStatus = null,
  extraControls,
  onSavedComments,
  bulkExportModalConfig,
  allowResultsCsvExport = true,
  reportAccessLevel = null
}): JSX.Element => {
  // Single state for managing the drawer
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false)
  const [isExporting, setIsExporting] = useState<boolean>(false)
  // Track selected export config for modal
  const [bulkExportConfig, setBulkExportConfig] = useState<ExportConfig | null>(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const { selectedTrend, dispatch } = useReportsContext()
  const handleBulkExport = async (exportConfig: ExportConfig): Promise<void> => {
    setIsExporting(true)
    setBulkExportConfig(exportConfig)
    try {
      if (exportConfig.fileType === 'PDF') {
        await downloadPDFReport(exportConfig)
      } else if (exportConfig.fileType === 'CSV') {
        await downloadCSVFile(exportConfig)
      }
      onCloseExportModal()
    } catch (error: any) {
      notification(error?.message ?? 'Failed to export', { variant: 'error' })
    } finally {
      setIsExporting(false)
    }
  }

  const requestForFilesEmailSubject = encodeURIComponent('Request for Course Section files')
  const requestForFilesEmailBody = encodeURIComponent(
    `Please complete the missing fields below (e.g., School Email, First Name, Last Name, File Format) in the email before sending.\n\nSchool Email: [Provide your School Email Here]\nFirst Name: [Provide your First Name Here]\nLast Name: [Prove your Last Name Here]\nFile Format: [Choose between PDF/CSV]\n\n---FOR DATA RETRIEVAL: DO NOT MODIFY THE SECTION BELOW---\nTerm: ${requestForCourseSectionFilesInfo?.termTitle ?? ''
    }\nSurvey Project: ${requestForCourseSectionFilesInfo?.surveyProjectTitle ?? ''}\nAcademic Unit: ${requestForCourseSectionFilesInfo?.divisionTitle ?? ''
    }\n`
  )
  const mailtoLink = `mailto:support@classranked.com?subject=${requestForFilesEmailSubject}&body=${requestForFilesEmailBody}`

  const [settingsAnchorEl, setSettingsAnchorEl] = React.useState<null | HTMLElement>(null)
  const isSettingsMenuOpen = Boolean(settingsAnchorEl)
  const closeSettingsMenu = (): void => {
    setSettingsAnchorEl(null)
  }

  // CR-341: the Sandbox Reports row lives here rather than in ReportActionsMenu, which returns
  // null for every active role but Account Owner and is not rendered at all on the instructor
  // reports page (`hideRegenerate`). Behind that gate the row's own `hasGlobalRole` test could
  // never fail, so a Global-role holder sitting in any other seat had no way to reach the
  // setting. This menu is the one the page can offer to all of them.
  const [showSandboxReportsModal, setShowSandboxReportsModal] = useState<boolean>(false)
  const { hasGlobalRole, activeRole } = useUserRole()
  // Reported in the row's chip; the change itself is confirmed in the modal.
  const { showSandboxOnly } = useSandboxReportsView()

  // The caller decides where Saved Comments belongs — it is an instructor route, and passing it
  // is the decision. `hideMoreButton` no longer gates it: it kept Saved Comments off the admin
  // reports page by hiding the whole button, which also hid everything else in this menu.
  const showSavedComments = onSavedComments != null
  const showPrint = !hideMoreButton
  // Regenerate/Revert/Restore. `ReportActionsMenu` renders nothing unless the active role is
  // Account Owner, so this mirrors its own gate to decide whether the divider and the button are
  // earned. `hideRegenerate` keeps them off the instructor reports page as before.
  const showReportActions = !hideRegenerate && activeRole?.roleName === ROLES.ACCOUNT_OWNER
  // The button follows the menu, not the other way round: it is offered only when something
  // inside it can be acted on. Print is inert, so it never justifies opening the menu on its own.
  // The Regenerate/Revert/Restore confirmation is held here, not in ReportActionsMenu: those rows
  // sit inside the popover, which unmounts them once it closes, and a modal they rendered went
  // with them a moment after it opened.
  const [openReportAction, setOpenReportAction] = useState<ReportActionKind | null>(null)
  const hasActionableSettingsItem = showSavedComments || hasGlobalRole || showReportActions

  const [exportAnchorEl, setExportAnchorEl] = React.useState<null | HTMLElement>(null)
  const isExportMenuOpen = Boolean(exportAnchorEl)
  const openExportMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setExportAnchorEl(event.currentTarget)
  }
  const closeExportMenu = (): void => {
    setExportAnchorEl(null)
  }

  const disabledInsightsDrawer = false

  const getDisplaySettingsTooltip = (): string => {
    if (disabledInsightsDrawer) {
      return 'Report insights are disabled because no reports are available.'
    }
    if (selectedTrend > 1) {
      return 'Report insights are disabled for multiple historical Terms selections.'
    }
    return ''
  }
  const displaySettingsTooltip = getDisplaySettingsTooltip()
  const exportTooltipTitle = `${disableButtons || !percent ? 'Reports can only be exported if there are responses. ' : ''}Export report.`
  const controlButtonWidth = isMobile ? '100%' : 'auto'
  const controlCellSx: SxProps<Theme> = isMobile
    ? {
        flex: '1 1 0',
        minWidth: 0,
        display: 'flex',
        '& > *': {
          width: '100%',
          display: 'flex'
        }
      }
    : { flex: '0 0 auto', minWidth: 'fit-content', display: 'flex' }
  const exportButton = (
    <GenericButton
      fullWidth={isMobile}
      label={isMobile ? undefined : 'Export'}
      aria-label="Export"
      variant="outlined"
      fontWeight="bold"
      buttonType={ButtonType.Secondary}
      onClick={onOpenExportModal}
      disabled={disableButtons || !percent}
      icon={isMobile ? <FileDownloadOutlinedIcon /> : undefined}
      style={{ width: controlButtonWidth, flexShrink: 0 }}
    />
  )
  return (
    <>
      {isDrawerOpen && (
        <DisplayCustomizationDrawer
          isModalOpen={isDrawerOpen}
          onClose={() => { setIsDrawerOpen(false) }}
          fetchReports={fetchReports}
          onSubmitDisplaySettings={onSubmitDisplaySettings}
        />
      )}
      <ReportControlButtons>
        <Stack
          direction='row'
          spacing={1}
          sx={{ width: '100%', flexWrap: 'nowrap', alignItems: 'stretch' }}
        >
          <Box data-testid="report-control-cell-export" sx={controlCellSx}>
            <CustomTooltip title={exportTooltipTitle}>
              {exportButton}
            </CustomTooltip>
          </Box>
          <Box data-testid="report-control-cell-insights" sx={controlCellSx}>
            <CustomTooltip title={displaySettingsTooltip}>
              <GenericButton
                fullWidth={isMobile}
                variant="outlined"
                buttonType={ButtonType.Secondary}
                aria-label="Report insights"
                disabled={disabledInsightsDrawer || (disableButtons ?? false) || selectedTrend > 1}
                icon={<InsightsOutlinedIcon />}
                onClick={() => { setIsDrawerOpen(true) }}
                style={{ width: controlButtonWidth, flexShrink: 0 }}
              />
            </CustomTooltip>
          </Box>
          {extraControls}
          {hasActionableSettingsItem && (
            <Box data-testid="report-control-cell-display-settings" sx={controlCellSx}>
              {/* Not gated on `disableButtons`. That flag means "no report is loaded", which is
                  the right guard for Export and Insights and the wrong one for everything in this
                  menu: Saved Comments navigates elsewhere, and Sandbox Reports changes which
                  Survey Projects the picker offers. Gating it made the setting unreachable in the
                  one state where a reader most needs it — an empty picker, because sandbox-only is
                  on and there are no rehearsals to show. */}
              <GenericButton
                fullWidth={isMobile}
                buttonType={ButtonType.Secondary}
                aria-label="Display settings"
                icon={<MoreVertIcon />}
                style={{ width: controlButtonWidth, flexShrink: 0 }}
                onClick={(e: React.MouseEvent<HTMLElement>) => { setSettingsAnchorEl(e.currentTarget) }}
              />
            </Box>
          )}
        </Stack>
      </ReportControlButtons>
      <BulkExportModal
        {...bulkExportModalConfig}
        isOpen={isBulkExportModalOpen}
        onClose={onCloseExportModal}
        onExport={(exportConfig) => { void handleBulkExport(exportConfig) }}
        isExporting={isExporting}
        disableOption={disableOption}
        allowCommentsExport={bulkExportModalConfig?.allowCommentsExport ?? selectedTrend === 1}
        initialExportBy={initialExportBy}
        lockExportBy={lockExportBy}
        allowResultsCsvExport={bulkExportModalConfig?.allowResultsCsvExport ?? allowResultsCsvExport}
        reportAccessLevel={bulkExportModalConfig?.reportAccessLevel ?? reportAccessLevel}
      />
      <Popover
        onClose={closeSettingsMenu}
        open={isSettingsMenuOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        id="settings-menu"
        anchorEl={settingsAnchorEl}
      >
        <List>
          {/* Report actions first: they act on the report in front of you. The rows below change
              this page or the picker, which is why a divider separates the two kinds. */}
          {showReportActions && (
            <ReportActionsMenu
              onSelect={(action) => { closeSettingsMenu(); setOpenReportAction(action) }}
              isSection={isSectionReport}
              meta={reportMeta}
              sectionSlotStatus={sectionSlotStatus}
            />
          )}
          {showReportActions && (showSavedComments || showPrint || hasGlobalRole) && <Divider />}
          {showSavedComments && (
            <ListItem disablePadding>
              <ListItemButton onClick={() => { onSavedComments?.(); closeSettingsMenu() }}>
                Saved Comments
              </ListItemButton>
            </ListItem>
          )}
          {showPrint && (
            <ListItem disabled disablePadding>
              <ListItemButton>Print</ListItemButton>
            </ListItem>
          )}
          {hasGlobalRole && (showSavedComments || showPrint) && <Divider />}
          {hasGlobalRole && (
            <ListItem disablePadding>
              {/* An On/Off chip row opening a confirmation modal — the Sync Course Sections
                  shape. The rows above act on this report or this page; this one changes which
                  Survey Projects the picker offers, hence the divider. */}
              <ListItemButton
                sx={{ gap: 1.5, justifyContent: 'space-between' }}
                onClick={() => { closeSettingsMenu(); setShowSandboxReportsModal(true) }}
              >
                Sandbox Reports
                <Chip
                  label={showSandboxOnly ? 'On' : 'Off'}
                  size="small"
                  color={showSandboxOnly ? 'warning' : 'default'}
                  sx={{ height: 20, fontSize: '11px', fontWeight: 600 }}
                />
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Popover>

      {openReportAction != null && (
        <ReportMutationAction
          isOpen
          action={openReportAction}
          onClose={() => { setOpenReportAction(null) }}
          onCompleted={() => { void fetchReports({}) }}
          targetName={reportMeta?.title}
          surveyProjectTitle={reportMeta?.surveyProjectTitle}
          termName={reportMeta?.termName}
          isSection={isSectionReport}
          targetVersion={openReportAction === 'restore-latest' ? reportMeta?.latestVersion?.version : undefined}
        />
      )}

      {showSandboxReportsModal && (
        <SandboxReportsViewModal
          isOpen
          handleCloseModal={() => { setShowSandboxReportsModal(false) }}
        />
      )}
    </>
  )
}

export default ReportsControls
